import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BriefcaseIcon,
  FolderIcon,
  BanknotesIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  StarIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  CalendarIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../context/ThemeContext';

const groupedNav = [
  {
    key: 'principal',
    title: '📊 PRINCIPAL',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: HomeIcon },
      { path: '/actividad', label: 'Actividad', icon: EyeIcon },
      {
        path: '/AsistenteIA',
        label: 'Asistente',
        icon: QuestionMarkCircleIcon,
      },
    ],
  },
  {
    key: 'empresa',
    title: '🏢 EMPRESA',
    items: [
      { path: '/empresas', label: 'Empresas', icon: BuildingOfficeIcon },
      { path: '/departamentos', label: 'Departamentos', icon: FolderIcon },
      { path: '/cargos', label: 'Cargos', icon: BriefcaseIcon },
    ],
  },
  {
    key: 'empleados',
    title: '👥 EMPLEADOS',
    items: [
      { path: '/empleados', label: 'Empleados', icon: UsersIcon },
      { path: '/usuarios', label: 'Usuarios', icon: ShieldCheckIcon },
      {
        path: '/tipos-contrato',
        label: 'Tipos Contrato',
        icon: DocumentTextIcon,
      },
      { path: '/perfiles', label: 'Perfiles', icon: UserGroupIcon },
    ],
  },
  {
    key: 'asistencias',
    title: '⏰ PLANNER',
    items: [
      { path: '/asistencias', label: 'Asistencias', icon: ClockIcon },
      { path: '/horarios', label: 'Horarios', icon: CalendarDaysIcon },
      { path: '/turnos', label: 'Turnos', icon: CalendarIcon },
      { path: '/rotaciones', label: 'Rotaciones', icon: ArrowPathIcon },
      { path: '/planificador', label: 'Planificador', icon: TableCellsIcon },
      { path: '/calendarios', label: 'Calendarios', icon: CalendarIcon },
      {
        path: '/aprobar-justificaciones',
        label: 'Aprobar Justificaciones',
        icon: ShieldCheckIcon,
      },
    ],
  },
  {
    key: 'nomina',
    title: '💰 NÓMINA',
    items: [
      { path: '/nomina', label: 'Nómina', icon: CurrencyDollarIcon },
      { path: '/reglas', label: 'Reglas de nómina', icon: DocumentTextIcon },
      { path: '/adelantos', label: 'Adelantos', icon: BanknotesIcon },
      { path: '/prestamos', label: 'Préstamos', icon: CreditCardIcon },
    ],
  },
  {
    key: 'desempeno',
    title: '🏆 DESEMPEÑO',
    items: [
      { path: '/premios', label: 'Premios', icon: StarIcon },
      { path: '/sanciones', label: 'Sanciones', icon: ExclamationTriangleIcon },
    ],
  },
  {
    key: 'reportes',
    title: '📈 REPORTES',
    items: [{ path: '/reportes', label: 'Reportes', icon: ChartBarIcon }],
  },
  {
    key: 'sistema',
    title: '🔧 SISTEMA',
    items: [
      {
        path: '/dispositivos',
        label: 'Dispositivos',
        icon: DevicePhoneMobileIcon,
      },
      { path: '/configuracion', label: 'Configuración', icon: Cog6ToothIcon },
      { path: '/ayuda', label: 'Ayuda', icon: QuestionMarkCircleIcon },
    ],
  },
];

const groupColors: Record<string, string> = {
  principal: '#0ea5a4',
  empresa: '#6366f1',
  empleados: '#f43f5e',
  asistencias: '#10b981',
  nomina: '#f59e0b',
  desempeno: '#ef4444',
  reportes: '#8b5cf6',
  sistema: '#06b6d4',
};

export const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const { isDark } = useTheme();
  const storageKey = 'sidebar.openGroups';

  const defaultState = groupedNav.reduce(
    (acc: Record<string, boolean>, g) => {
      acc[g.key] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw || '{}');
        return { ...defaultState, ...parsed };
      }
    } catch (e) {
      // ignore parse errors
    }
    return defaultState;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(openGroups));
    } catch (e) {
      // ignore storage errors
    }
  }, [openGroups]);

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <motion.aside
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className="fixed left-0 top-0 h-screen w-64 border-r flex flex-col z-40"
      style={{
        background: isDark
          ? 'linear-gradient(to bottom, var(--color-background-dark), var(--color-surface-dark))'
          : 'linear-gradient(to bottom, var(--color-background-light), var(--color-surface-light))',
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgb(229,231,235)',
      }}
    >
      {/* Logo */}
      <div
        className="h-16 flex items-center px-6 border-b"
        style={{
          borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgb(229,231,235)',
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <span className="text-white font-bold text-sm">N</span>
          </div>
          <span
            className="font-semibold text-lg"
            style={{
              color: isDark
                ? 'var(--color-text-dark, #e8eaf6)'
                : 'var(--color-text-light, #1a237e)',
            }}
          >
            Nómina
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <ul className="space-y-2">
          {groupedNav.map((group) => (
            <li key={group.key}>
              <div
                className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 cursor-pointer"
                onClick={() => toggleGroup(group.key)}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-1.5 h-6 rounded mr-2"
                    style={{
                      background: groupColors[group.key] || 'transparent',
                    }}
                  />
                  <span
                    className="text-sm md:text-base font-bold"
                    style={{
                      color: isDark
                        ? 'var(--color-text-dark, #e8eaf6)'
                        : groupColors[group.key] ||
                          'var(--color-text-light, #1a237e)',
                    }}
                  >
                    {group.title}
                  </span>
                </div>
                <ChevronDownIcon
                  className={`w-4 h-4 transform transition-transform ${openGroups[group.key] ? 'rotate-180' : 'rotate-0'}`}
                />
              </div>

              {openGroups[group.key] && (
                <ul className="mt-1 space-y-1">
                  {group.items.map((item) => (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                            isActive
                              ? 'bg-gradient-to-r from-primary to-accent text-white'
                              : 'hover:bg-gray-100 dark:hover:bg-white/5'
                          }`
                        }
                        style={({ isActive }) => ({
                          color: isActive
                            ? 'white'
                            : isDark
                              ? 'var(--color-text-dark, #e8eaf6)'
                              : 'var(--color-text-light, #1a237e)',
                        })}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="text-sm font-medium">
                          {item.label}
                        </span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* User section */}
      <div
        className="p-4 border-t"
        style={{
          borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgb(229,231,235)',
        }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <span className="text-white font-bold text-sm">
              {user?.nombre?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-sm font-medium truncate"
              style={{
                color: isDark
                  ? 'var(--color-text-dark, #e8eaf6)'
                  : 'var(--color-text-light, #1a237e)',
              }}
            >
              {user?.nombre || 'Usuario'}
            </p>
            <p className="text-gray-500 text-xs truncate">
              {user?.email || ''}
            </p>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
        >
          <ArrowRightOnRectangleIcon className="w-4 h-4" />
          <span className="text-sm">Cerrar Sesión</span>
        </button>
      </div>
    </motion.aside>
  );
};
